import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSeo } from '../hooks/useSeo';
import { fetchCollection } from '../lib/cms';
import ArticleCard from '../components/ArticleCard';
import HelpNotice from '../components/HelpNotice';

export default function ArticlesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryParam = searchParams.get('kategori') || '';
  const audienceParam = searchParams.get('maalgruppe') || '';

  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useSeo({
    title: 'Artikler',
    description: 'Skatunderlups artikelarkiv: Artikler, analyser, baggrundsrapporter og kommentarer om skat, moms og afgifter for private og erhverv.',
  });

  useEffect(() => {
    let isMounted = true;
    async function loadArticles() {
      setLoading(true);
      try {
        const res = await fetchCollection('articles', {
          sort: 'publish_date',
          order: 'desc',
          limit: 50,
        });
        if (isMounted) {
          setArticles(res.items || []);
          setLoading(false);
        }
      } catch (err) {
        console.warn('Failed to load articles', err);
        if (isMounted) setLoading(false);
      }
    }
    loadArticles();
    return () => { isMounted = false; };
  }, []);

  // Filter articles client-side based on parameters and search
  const filteredArticles = articles.filter((art) => {
    // Audience filter
    if (audienceParam && audienceParam !== 'alle') {
      if (audienceParam === 'privat' && art.audience !== 'privat' && art.audience !== 'begge') return false;
      if (audienceParam === 'erhverv' && art.audience !== 'erhverv' && art.audience !== 'begge') return false;
    }

    // Category filter
    if (categoryParam) {
      const artCategories = art.terms?.category || [];
      if (!artCategories.includes(categoryParam)) return false;
    }

    // Text search query
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const matchTitle = (art.title || '').toLowerCase().includes(q);
      const matchIntro = (art.intro || '').toLowerCase().includes(q);
      const matchSub = (art.subtitle || '').toLowerCase().includes(q);
      const matchAuthor = (art.author || '').toLowerCase().includes(q);
      if (!matchTitle && !matchIntro && !matchSub && !matchAuthor) return false;
    }

    return true;
  });

  const handleAudienceChange = (aud) => {
    const next = new URLSearchParams(searchParams);
    if (aud === 'alle' || !aud) {
      next.delete('maalgruppe');
    } else {
      next.set('maalgruppe', aud);
    }
    setSearchParams(next);
  };

  const handleCategoryChange = (cat) => {
    const next = new URLSearchParams(searchParams);
    if (!cat) {
      next.delete('kategori');
    } else {
      next.set('kategori', cat);
    }
    setSearchParams(next);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Archive Header */}
      <div className="border-b border-slate-200 bg-slate-50/70 py-8 px-4">
        <div className="mx-auto max-w-7xl">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#fd7302]">
            <i className="fa-solid fa-newspaper" aria-hidden="true" />
            <span>Redaktionelt arkiv</span>
          </div>
          <h1 className="mt-1 font-serif text-3xl sm:text-4xl lg:text-5xl font-black text-[#010b27]">
            Artikler & Analyser
          </h1>
          <p className="mt-2 max-w-2xl text-sm sm:text-base text-slate-600">
            Uafhængig skattejournalistik, lovreformer, administrative afgørelser og vejledning for både private borgere og erhvervslivet.
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-8">
        {/* Filters and Search Bar */}
        <div className="mb-8 rounded-md border border-slate-200 bg-white p-4 sm:p-6 shadow-xs">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
            {/* Search Input */}
            <div className="md:col-span-4">
              <label htmlFor="search" className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">
                Søg i artikler
              </label>
              <div className="relative">
                <input
                  id="search"
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Søg på emne, ord eller forfatter..."
                  className="w-full rounded-sm border border-slate-200 bg-white py-2 pl-9 pr-3 text-xs sm:text-sm text-[#010b27] placeholder:text-slate-400 focus:border-[#fd7302] focus:outline-none focus:ring-1 focus:ring-[#fd7302]"
                />
                <i className="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-xs text-slate-400" aria-hidden="true" />
              </div>
            </div>

            {/* Audience Segment */}
            <div className="md:col-span-4">
              <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">
                Målgruppe
              </label>
              <div className="inline-flex w-full rounded-sm border border-slate-200 bg-white p-0.5">
                <button
                  type="button"
                  onClick={() => handleAudienceChange('alle')}
                  className={`flex-1 py-1.5 text-xs font-semibold rounded-xs transition-colors ${
                    !audienceParam || audienceParam === 'alle'
                      ? 'bg-[#010b27] text-white'
                      : 'text-slate-600 hover:text-[#010b27]'
                  }`}
                >
                  Alle
                </button>
                <button
                  type="button"
                  onClick={() => handleAudienceChange('privat')}
                  className={`flex-1 py-1.5 text-xs font-semibold rounded-xs transition-colors ${
                    audienceParam === 'privat'
                      ? 'bg-[#010b27] text-white'
                      : 'text-slate-600 hover:text-[#010b27]'
                  }`}
                >
                  Private
                </button>
                <button
                  type="button"
                  onClick={() => handleAudienceChange('erhverv')}
                  className={`flex-1 py-1.5 text-xs font-semibold rounded-xs transition-colors ${
                    audienceParam === 'erhverv'
                      ? 'bg-[#fd7302] text-white'
                      : 'text-slate-600 hover:text-[#fd7302]'
                  }`}
                >
                  Erhverv
                </button>
              </div>
            </div>

            {/* Category Select */}
            <div className="md:col-span-4">
              <label htmlFor="kategori" className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">
                Kategori
              </label>
              <select
                id="kategori"
                value={categoryParam}
                onChange={(e) => handleCategoryChange(e.target.value)}
                className="w-full rounded-sm border border-slate-200 bg-white py-2 px-3 text-xs sm:text-sm text-[#010b27] focus:border-[#fd7302] focus:outline-none focus:ring-1 focus:ring-[#fd7302]"
              >
                <option value="">Alle faglige kategorier</option>
                <option value="moms-afgifter">Moms & Afgifter</option>
                <option value="indkomstskat">Indkomstskat & Fradrag</option>
                <option value="selskabsskat">Selskabsskat & Erhverv</option>
                <option value="skattekontrol">Skattekontrol & Tilsyn</option>
                <option value="ejendomsskat">Bolig & Ejendomsskat</option>
                <option value="presse-debat">Presse & Debat</option>
              </select>
            </div>
          </div>

          {/* Active filter pills */}
          {(categoryParam || audienceParam || searchQuery) && (
            <div className="mt-4 pt-3 border-t border-slate-100 flex flex-wrap items-center gap-2 text-xs text-slate-600">
              <span className="font-semibold text-[#010b27]">Aktive filtre:</span>
              {audienceParam && (
                <span className="inline-flex items-center gap-1 bg-slate-100 px-2 py-0.5 rounded-sm">
                  Målgruppe: {audienceParam}
                  <button type="button" onClick={() => handleAudienceChange('alle')} className="hover:text-red-700">
                    <i className="fa-solid fa-xmark text-[10px]" aria-hidden="true" />
                  </button>
                </span>
              )}
              {categoryParam && (
                <span className="inline-flex items-center gap-1 bg-slate-100 px-2 py-0.5 rounded-sm">
                  Kategori: {categoryParam}
                  <button type="button" onClick={() => handleCategoryChange('')} className="hover:text-red-700">
                    <i className="fa-solid fa-xmark text-[10px]" aria-hidden="true" />
                  </button>
                </span>
              )}
              {searchQuery && (
                <span className="inline-flex items-center gap-1 bg-slate-100 px-2 py-0.5 rounded-sm">
                  Søgning: &quot;{searchQuery}&quot;
                  <button type="button" onClick={() => setSearchQuery('')} className="hover:text-red-700">
                    <i className="fa-solid fa-xmark text-[10px]" aria-hidden="true" />
                  </button>
                </span>
              )}
              <button
                type="button"
                onClick={() => {
                  setSearchParams({});
                  setSearchQuery('');
                }}
                className="text-[#fd7302] underline font-medium hover:text-[#e06500] ml-auto text-xs"
              >
                Nulstil alle filtre
              </button>
            </div>
          )}
        </div>

        {/* Results Counter */}
        <div className="flex items-center justify-between pb-3 mb-6 border-b border-slate-200">
          <h2 className="font-serif text-xl font-bold text-[#010b27]">
            Fundne artikler ({filteredArticles.length})
          </h2>
        </div>

        {/* Articles Grid */}
        {loading ? (
          <div className="py-20 text-center font-serif text-slate-500">
            Henter arkivindhold...
          </div>
        ) : filteredArticles.length === 0 ? (
          <div className="rounded-md border border-dashed border-slate-200 p-12 text-center bg-slate-50/50">
            <i className="fa-solid fa-newspaper text-3xl text-slate-400 mb-3" aria-hidden="true" />
            <h3 className="font-serif text-xl font-bold text-[#010b27]">
              Ingen artikler matcher dine valgte filtre
            </h3>
            <p className="mt-1 text-sm text-slate-600">
              Prøv at fjerne eller ændre dine søgekriterier for at se flere historier.
            </p>
            <button
              type="button"
              onClick={() => {
                setSearchParams({});
                setSearchQuery('');
              }}
              className="mt-4 inline-flex items-center gap-1.5 rounded-sm bg-[#010b27] px-4 py-2 text-xs font-semibold text-white hover:bg-[#fd7302]"
            >
              Nulstil filtre
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredArticles.map((art) => (
              <ArticleCard key={art.id} article={art} variant="grid" />
            ))}
          </div>
        )}

        {/* Bottom Banner */}
        <div className="mt-16">
          <HelpNotice variant="banner" />
        </div>
      </div>
    </div>
  );
}
